import { useState } from 'react';
import { MagnifyingGlassIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { getIconByName, iconCategories, searchIcons } from '../../utils/iconMapping';

const IconPicker = ({ selectedIcon, onIconSelect, onClose }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [activeCategory, setActiveCategory] = useState('all');
  const [showSearch, setShowSearch] = useState(false);

  const getIconsToDisplay = () => {
    if (showSearch && searchTerm) {
      return searchIcons(searchTerm);
    }
    return iconCategories[activeCategory]?.icons || [];
  };

  const handleIconClick = (iconName) => {
    onIconSelect(iconName);
    onClose();
  };

  const iconsToDisplay = getIconsToDisplay();

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999]">
      <div className="bg-base-100 rounded-lg shadow-xl max-w-5xl w-[90%] max-w-[1200px] max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-base-300 flex items-center justify-between">
          <h3 className="text-lg font-semibold">Choose an Icon</h3>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setShowSearch(!showSearch)}
              className={`btn btn-sm ${showSearch ? 'btn-primary' : 'btn-ghost'}`}
              title="Toggle search"
            >
              <MagnifyingGlassIcon className="w-4 h-4" />
            </button>
            <button
              onClick={onClose}
              className="btn btn-sm btn-ghost"
              title="Close"
            >
              <XMarkIcon className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Search Bar */}
        {showSearch && (
          <div className="p-4 border-b border-base-300">
            <div className="relative">
              <input
                type="text"
                placeholder="Search icons..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="input input-bordered w-full pl-10"
                autoFocus
              />
              <MagnifyingGlassIcon className="w-5 h-5 absolute left-3 top-1/2 transform -translate-y-1/2 text-base-content/50" />
            </div>
          </div>
        )}

        {/* Category Tabs */}
        {!showSearch && (
          <div className="px-4 border-b border-base-300">
            <div className="flex overflow-x-auto gap-1 py-2">
              {Object.entries(iconCategories).map(([key, category]) => (
                <button
                  key={key}
                  onClick={() => setActiveCategory(key)}
                  className={`btn btn-sm btn-ghost whitespace-nowrap flex-shrink-0 ${
                    activeCategory === key ? 'btn-active' : ''
                  }`}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Selected Icon Preview */}
        {selectedIcon && (
          <div className="p-4 bg-base-50 border-b border-base-300">
            <div className="flex items-center space-x-3">
              <span className="text-sm text-base-content/60">Current selection:</span>
              <div className="flex items-center space-x-2 px-3 py-2 bg-primary/10 rounded-lg">
                {getIconByName(selectedIcon, "w-5 h-5 text-primary")}
                <span className="text-sm font-medium text-primary">{selectedIcon}</span>
              </div>
            </div>
          </div>
        )}

        {/* Icons Grid */}
        <div className="flex-1 overflow-auto p-4">
          {iconsToDisplay.length === 0 ? (
            <div className="text-center py-12">
              <div className="text-base-content/50">
                {showSearch ? 'No icons found for your search.' : 'No icons in this category.'}
              </div>
            </div>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-5 md:grid-cols-6 lg:grid-cols-8 xl:grid-cols-10 gap-4">
              {iconsToDisplay.map((iconName) => (
                <button
                  key={iconName}
                  onClick={() => handleIconClick(iconName)}
                  className={`
                    p-2 rounded-lg border-2 transition-all duration-200 
                    hover:shadow-md hover:scale-105 group overflow-hidden
                    ${selectedIcon === iconName 
                      ? 'border-primary bg-primary/10 shadow-md' 
                      : 'border-base-300 hover:border-primary/50 bg-base-100 hover:bg-base-50'
                    }
                  `}
                  title={iconName}
                >
                  <div className="flex flex-col items-center space-y-1">
                    {getIconByName(iconName, `w-5 h-5 ${
                      selectedIcon === iconName 
                        ? 'text-primary' 
                        : 'text-base-content group-hover:text-primary'
                    }`)}
                    <span className={`text-xs font-medium text-center leading-tight truncate max-w-full ${
                      selectedIcon === iconName 
                        ? 'text-primary' 
                        : 'text-base-content/60 group-hover:text-base-content'
                    }`}>
                      {iconName.replace('Icon', '')}
                    </span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-base-300 bg-base-50">
          <div className="flex justify-between items-center text-sm text-base-content/60">
            <span>
              {showSearch && searchTerm 
                ? `${iconsToDisplay.length} icons found`
                : `${iconsToDisplay.length} icons in ${iconCategories[activeCategory]?.label}`
              }
            </span>
            <span>Click an icon to select it</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IconPicker;